package kz.dentalcrm.app.screens.services.detail

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.safeDrawingPadding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.BottomSheetDefaults
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.Text
import androidx.compose.material3.rememberModalBottomSheetState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import compose.icons.FeatherIcons
import compose.icons.feathericons.Clock
import compose.icons.feathericons.DollarSign
import compose.icons.feathericons.FilePlus
import compose.icons.feathericons.FileText
import dentalcrm.composeapp.generated.resources.Res
import dentalcrm.composeapp.generated.resources.add_patient_title
import dentalcrm.composeapp.generated.resources.delete
import dentalcrm.composeapp.generated.resources.save
import dentalcrm.composeapp.generated.resources.service_add
import dentalcrm.composeapp.generated.resources.service_naming
import dentalcrm.composeapp.generated.resources.service_update
import dentalcrm.composeapp.generated.resources.service_update_title
import dentalcrm.composeapp.generated.resources.service_category
import dentalcrm.composeapp.generated.resources.service_description
import dentalcrm.composeapp.generated.resources.service_duration
import dentalcrm.composeapp.generated.resources.service_price
import dentalcrm.composeapp.generated.resources.update
import kz.dentalcrm.app.core.ui.UiStateScaffold
import kz.dentalcrm.app.core.ui.components.UIButton
import kz.dentalcrm.app.core.ui.components.UITextInput
import kz.dentalcrm.app.core.ui.icons.DefaultIcons
import kz.dentalcrm.app.core.ui.icons.defaulticons.Tenge
import kz.dentalcrm.app.data.network.response_model.services.ServiceItem
import kz.dentalcrm.app.screens.services.detail.mvi.EditServiceState
import kz.dentalcrm.app.screens.services.detail.mvi.EditServiceViewEvent
import kz.dentalcrm.app.screens.services.services.ServiceCategoryItem
import kz.dentalcrm.app.theme.DefaultTheme
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun EditServiceDialog(
    item: ServiceItem,
    onEdit: () -> Unit,
    onDelete: (Int) -> Unit,
    onDismiss: () -> Unit,
) {
    val modalBottomSheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true)
    val screenModel = koinViewModel<EditServiceViewModel>()
    val state by screenModel.viewState.collectAsState()

    LaunchedEffect(state.screenState) {
        when (state.screenState) {
            is EditServiceState.ScreenState.Added -> {
                screenModel.handleEvents(EditServiceViewEvent.Reset)
                onEdit()
                onDismiss()
            }
            is EditServiceState.ScreenState.Deleted -> {
                screenModel.handleEvents(EditServiceViewEvent.Reset)
                onDelete(item.id)
                onDismiss()
            }
            else -> Unit
        }
    }

    ModalBottomSheet(
        modifier = Modifier.statusBarsPadding().imePadding(),
        onDismissRequest = {
            screenModel.handleEvents(EditServiceViewEvent.Reset)
            onDismiss()
        },
        sheetState = modalBottomSheetState,
        dragHandle = { BottomSheetDefaults.DragHandle() },
        containerColor = DefaultTheme.colors.surfaceSecondary,
        content = {
            UiStateScaffold(
                isLoading = state.screenState is EditServiceState.ScreenState.Loading,
                modifier = Modifier.background(Color.Transparent),
                content = {
                    EditServiceContent(
                        item = item,
                        state = state,
                        event = screenModel::handleEvents
                    )
                }
            )
        }
    )
}

@Composable
private fun EditServiceContent(
    item: ServiceItem,
    state: EditServiceState,
    event: (EditServiceViewEvent) -> Unit,
) {
    Column(
        modifier = Modifier
            .background(
                color = DefaultTheme.colors.surfaceSecondary,
            )
            .verticalScroll(rememberScrollState())
            .fillMaxWidth(),
    ) {

        LaunchedEffect("key") {
            item.category?.let { category ->
                event(EditServiceViewEvent.CategorySelected(category))
            }
        }

        var name by remember { mutableStateOf(item.name ?: "") }
        var description by remember { mutableStateOf(item.description ?: "") }
        var duration by remember { mutableStateOf((item.duration ?: 0).toString()) }
        var price by remember { mutableStateOf((item.price ?: 0).toString()) }

        Text(
            modifier = Modifier.padding(horizontal = 16.dp),
            text = stringResource(Res.string.service_update_title),
            style = DefaultTheme.typography.bodyLargeMedium,
            color = DefaultTheme.colors.onSurface
        )
        Spacer(modifier = Modifier.fillMaxWidth().height(16.dp))
        HorizontalDivider(
            modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp),
            thickness = 1.dp,
            color = DefaultTheme.colors.onSurfaceVariant.copy(
                alpha = 0.2f
            )
        )
        Spacer(modifier = Modifier.fillMaxWidth().height(16.dp))

        UITextInput(
            modifier = Modifier
                .padding(horizontal = 16.dp)
                .fillMaxWidth(),
            defaultValue = name,
            leadingIcon = FeatherIcons.FilePlus,
            label = stringResource(Res.string.service_naming),
        ) {
            name = it
        }

        Spacer(modifier = Modifier.fillMaxWidth().height(8.dp))
        UITextInput(
            modifier = Modifier
                .padding(horizontal = 16.dp)
                .heightIn(
                    min = 120.dp
                )
                .fillMaxWidth(),
            leadingIcon = FeatherIcons.FileText,
            singleLine = false,
            defaultValue = description,
            maxLines = 6,
            label = stringResource(Res.string.service_description),
        ) {
            description = it
        }

        Spacer(modifier = Modifier.fillMaxWidth().height(8.dp))
        UITextInput(
            modifier = Modifier
                .padding(horizontal = 16.dp)
                .fillMaxWidth(),
            leadingIcon = FeatherIcons.Clock,
            keyboardType = KeyboardType.Number,
            defaultValue = duration,
            label = stringResource(Res.string.service_duration),
        ) {
            duration = it
        }

        Spacer(modifier = Modifier.fillMaxWidth().height(8.dp))
        UITextInput(
            modifier = Modifier
                .padding(horizontal = 16.dp)
                .fillMaxWidth(),
            leadingIcon = DefaultIcons.Tenge,
            defaultValue = price,
            keyboardType = KeyboardType.Number,
            label = stringResource(Res.string.service_price),
        ) {
            price = it
        }

        Spacer(modifier = Modifier.fillMaxWidth().height(16.dp))
        Text(
            modifier = Modifier.padding(horizontal = 16.dp),
            text = stringResource(Res.string.service_category),
            style = DefaultTheme.typography.bodySmallMedium,
            color = DefaultTheme.colors.onSurface
        )
        Spacer(modifier = Modifier.fillMaxWidth().height(4.dp))
        LazyRow(
            modifier = Modifier
        ) {
            item {
                Spacer(
                    modifier = Modifier.width(12.dp)
                )
            }
            items(items = state.categories, key = { it.data.id }) {
                ServiceCategoryItem(
                    item = it
                ) {
                    event(
                        EditServiceViewEvent.CategorySelected(
                            category = it.data
                        )
                    )
                }
                Spacer(
                    modifier = Modifier.width(8.dp)
                )
            }
            item {
                Spacer(
                    modifier = Modifier.width(12.dp)
                )
            }
        }

        Spacer(modifier = Modifier.fillMaxWidth().height(16.dp))
        Row(
            modifier = Modifier
                .padding(horizontal = 16.dp)
                .fillMaxWidth()
                .imePadding(),
            horizontalArrangement = Arrangement.Center
        ) {
            UIButton(
                modifier = Modifier.weight(1f),
                containerColor = DefaultTheme.colors.error,
                text = stringResource(Res.string.delete),
                isLoading = state.screenState is EditServiceState.ScreenState.Adding,
                onClick = {
                    event(EditServiceViewEvent.DeleteService(id = item.id))
                },
            )
            Spacer(modifier = Modifier.width(8.dp))
            UIButton(
                modifier = Modifier.weight(1f),
                enabled = name.isNotEmpty() && description.isNotEmpty()
                        && duration.isNotEmpty() && price.isNotEmpty()
                        && state.categories.any { it.selected },
                text = stringResource(Res.string.update),
                isLoading = state.screenState is EditServiceState.ScreenState.Adding,
                onClick = {
                    event(
                        EditServiceViewEvent.EditService(
                            id = item.id,
                            naming = name,
                            description = description,
                            duration = duration,
                            price = price
                        )
                    )
                },
            )
        }

        Spacer(modifier = Modifier.fillMaxWidth().height(32.dp).navigationBarsPadding())
    }
}